import { useState, type FormEvent } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Handover } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';

dayjs.extend(relativeTime);

export default function Handovers() {
  const [incomingName, setIncomingName] = useState('');
  const [lastMedAt, setLastMedAt] = useState<Dayjs | null>(null);
  const [nextMedAt, setNextMedAt] = useState<Dayjs | null>(null);
  const [moodNote, setMoodNote] = useState('');
  const [bodyNote, setBodyNote] = useState('');
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['handovers'],
    queryFn: () => api.get<Handover[]>('/api/handovers?limit=50'),
  });

  const mutation = useMutation({
    mutationFn: (payload: {
      incomingName: string | null;
      lastMedAt: string | null;
      nextMedAt: string | null;
      moodNote: string | null;
      bodyNote: string;
    }) => api.post<Handover>('/api/handovers', payload),
    onSuccess: () => {
      setIncomingName('');
      setLastMedAt(null);
      setNextMedAt(null);
      setMoodNote('');
      setBodyNote('');
      qc.invalidateQueries({ queryKey: ['handovers'] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!bodyNote.trim()) return;
    mutation.mutate({
      incomingName: incomingName.trim() || null,
      lastMedAt: lastMedAt?.toISOString() ?? null,
      nextMedAt: nextMedAt?.toISOString() ?? null,
      moodNote: moodNote.trim() || null,
      bodyNote: bodyNote.trim(),
    });
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>Shift handovers</Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>New handover</Typography>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Handing over to (optional)"
              value={incomingName}
              onChange={(e) => setIncomingName(e.target.value)}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker
                label="Last medication"
                value={lastMedAt}
                onChange={setLastMedAt}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DateTimePicker
                label="Next medication due"
                value={nextMedAt}
                onChange={setNextMedAt}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
            <TextField
              label="Mood / current state"
              value={moodNote}
              onChange={(e) => setMoodNote(e.target.value)}
            />
            <TextField
              label="Notes for the next carer"
              multiline
              minRows={4}
              value={bodyNote}
              onChange={(e) => setBodyNote(e.target.value)}
              required
            />
            {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
            <Box>
              <Button type="submit" variant="contained" disabled={mutation.isPending || !bodyNote.trim()}>
                {mutation.isPending ? 'Saving…' : 'Save handover'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>Recent</Typography>
      <Stack spacing={1.5}>
        {(listQuery.data ?? []).map((h) => (
          <Paper key={h.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {dayjs(h.occurredAt).format('ddd DD MMM HH:mm')} · {h.authorName}
              {h.incomingName ? ` → ${h.incomingName}` : ''}
            </Typography>
            {(h.lastMedAt || h.nextMedAt) && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {h.lastMedAt && <>Last med: {dayjs(h.lastMedAt).format('HH:mm')} </>}
                {h.nextMedAt && <>· Next: {dayjs(h.nextMedAt).format('HH:mm')}</>}
              </Typography>
            )}
            {h.moodNote && (
              <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                {h.moodNote}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {h.bodyNote}
            </Typography>
          </Paper>
        ))}
        {(listQuery.data ?? []).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No handovers yet.</Box>
        )}
      </Stack>
    </Box>
  );
}
